import { IPromise } from 'angular';
import * as _ from 'underscore';

import { appStore } from '../../app/AppStore';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { ServerUrls } from '../../app/ServerUrls';
import { UrlParamKeys } from '../../app/UrlParamKeys';
import { Tableau } from '../../common/utilities/TableauLog';
import { Helpers } from '../helpers/Helpers';
import { IDataAlertInfo } from '../models/IDataAlertInfo';
import { IViz, VizType } from '../models/Viz';
import { Module } from '../Module';
import { DataAlerts } from '../resources/DataAlerts';
import { ServerService } from './ServerService';
import { WindowLocationService } from './WindowLocationService';

export let VizServiceEvents = {
  VizLoadComplete: 'vizloadedcomplete'
};

export class VizService {

  public static $inject = [
    '$state',
    '$location',
    'WindowLocationService',
    'ServerService',
    'DataAlerts'
  ];

  public static ExplicitOriginalVizParamKey = ':original_view';
  public static InstanceIdKey = ':iid';
  private static EmbedParamKey = ':embed';
  private static FormatParamKey = ':format';
  private static IsGuestRedirectFromVizPortalParamKey = ':isGuestRedirectFromVizportal';
  private static showShareParamKey = ':showShareOptions';

  private static VizExportFileTypeRegex = /\.(pdf|csv|png)$/;
  private static VizExportFormatTypeRegex = /(pdf|csv|png)$/;

  private didAlreadyDoRedirect = false;

  constructor(
    private $state: ng.ui.IStateService,
    private $location: ng.ILocationService,
    private WindowLocationService: WindowLocationService,
    private server: ServerService,
    private dataAlerts: DataAlerts,
  ) {}

  /**
   * Reload the current state when the location changes. This is needed for two reasons
   * 1. The query string parameters are not registered as $stateParams, so ui-router won't reload the state
   *    if they change. We need to make it reload in this case.
   * 2. If the $location changes but the $stateParams are identical to the $stateParams we originally entered
   *    the $state with, then ui-router will not reload the $state. This can happen if a user types in url to
   *    viz with vizPath `workbook/view1`, switches tabs by clicking on view2 so the url updates, then uses
   *    the url to switch back to `workbook/view1`. Because `workbook/view1` is the $stateParam when we entered
   *    the $state, ui-router will assume that nothing is changed and it won't reload. We force it to.
   */
  public reloadOnLocationChange($scope: ng.IScope) {
    // reloadOnSearch must be true otherwise ui-router will not reload the state. Ever. No matter what you do.
    this.$state.current.reloadOnSearch = true;
    $scope.$on('$locationChangeSuccess', () => {
      if (this.$state.current.reloadOnSearch) {
        /// TODO TFS949507

        // We need to get the new state params. $state.$current gives us access to the internal state of ui-router.
        // It's partially documented in ui-router's documentation.
        const currentStateUrlMatcher = (<any>this.$state).$current.url;
        const newStateParams = currentStateUrlMatcher.exec(this.$location.path(), this.$location.search());

        if (newStateParams) {
          // This transitionTo is a very particular hardReload.
          // location: false - tell ui-router to not update the location. Well, that's what you'd expect. ui-router
          //                   will still update the url if the original state's stateParams are different from
          //                   what's in the current url, but at least ui-router won't obliterate the query string
          //                   with `location: false`. This is good enough for our purposes.
          // reload: true - force ui-router to reload the view and controller
          this.$state.transitionTo(this.$state.current.name, newStateParams, {location: false, reload: true});
        }
      }
    });
  }

  /**
   * Change $location due to a change in a url parameter
   */
  public updateVizPath($scope: ng.IScope, path: string) {
    const siteUrls = siteUrlsSelector(appStore.getState());
    const newPath = siteUrls.viewPath(path);
    this.updateLocationPathToGivenPath($scope, newPath);
  }

  private updateLocationPathToGivenPath($scope: ng.IScope, newLocationPath: string) {
    // Don't change location if path is unchanged
    if (newLocationPath === this.$location.path()) {
      return;
    }

    this.dontReloadNextLocationChange($scope);

    this.$location.replace().path(newLocationPath);
  }

  public updateInstanceId($scope: ng.IScope, instanceId: string) {
    this.dontReloadNextLocationChange($scope);
    this.$location.replace().search(VizService.InstanceIdKey, instanceId);
  }

  public updateExplicitOriginalViz($scope: ng.IScope, explicitOriginalViz: boolean) {
    this.dontReloadNextLocationChange($scope);
    this.$location.replace().search(VizService.ExplicitOriginalVizParamKey, explicitOriginalViz ? 'y' : null);
  }

  public removeEditDataAlertUrlParam($scope: ng.IScope) {
    this.dontReloadNextLocationChange($scope);
    $scope.$apply(() => {
      this.$location.search(UrlParamKeys.EditDataAlert, null);
      this.$location.search(UrlParamKeys.ResumeDataAlert, null);
    });
  }

  public getIsExplicitOriginalViz(): boolean {
    return Helpers.BooleanQueryParamRegex.test(this.getExplicitLocationSearchValue(VizService.ExplicitOriginalVizParamKey));
  }

  public getInstanceId(): string {
    return this.getExplicitLocationSearchValue(VizService.InstanceIdKey);
  }

  public getDataAlertId(): string {
    return this.getExplicitLocationSearchValue(UrlParamKeys.EditDataAlert);
  }

  // Returns query parameter value, or null if there is no explicit value.
  // e.g. "?:embed" returns null, ":embed=blah" returns "blah"
  // Using $location.search() directly will give boolean true for keys that
  // are present with no value.
  public getExplicitLocationSearchValue(key: string): string {
    const value = this.$location.search()[key];
    return _.isString(value) ? value : null;
  }

  public getVizParams(): _.Dictionary<any> {
    // Remove the instance id param, as it is only used on the frontend
    return _.omit(this.$location.search(), VizService.InstanceIdKey);
  }

  public getIsExplicitOriginalVizFromUrl(url: string): boolean {
    // TODO: we should parse a viz object from a url instead of having several of these
    // one-off functions to get individual parts of the url. Can copy angular's url parsing code
    const match = decodeURIComponent(url).match(/[&\?]:original_view=([^&#$]+)/);
    return !!(match && match[1] && Helpers.BooleanQueryParamRegex.test(match[1]));
  }

  /**
   * Build a JS-API-ready uri from a viz object
   */
  public getUri(viz: IViz): string {
    const pathList: string[] = [this.WindowLocationService.getOrigin()];

    if (viz.vizType !== VizType.Admin && !!viz.siteUrlName) {
      pathList.push('t');
      pathList.push(viz.siteUrlName);
    }

    switch (viz.vizType) {
      case VizType.Admin:
        pathList.push('vizql');
        pathList.push('showadminview');
        break;
      case VizType.View:
        pathList.push('views');
        break;
      case VizType.RevisionPreview:
        pathList.push('workbooks');
        break;
      case VizType.DeviceVizPreview:
        pathList.push('views');
        break;
      default:
        throw new Error('Invalid vizType. Viz must be View, Admin, RevisionPreview, or DeviceVizPreview');
    }

    pathList.push(viz.path);
    return pathList.join('/');
  }

  /**
   * returns true if embed search param is on in url
   * Example: ?:embed=yes
   */
  public isEmbedParamOn() {
    return Helpers.BooleanQueryParamRegex.test(this.getExplicitLocationSearchValue(VizService.EmbedParamKey));
  }

  /**
   * returns true if showShareOptions search param is on in url
   * Example: ?:showShareOptions=yes
   */
  public isShowShareParamOn() {
    if (this.getExplicitLocationSearchValue(VizService.showShareParamKey) !== null) {
      return Helpers.BooleanQueryParamRegex.test(this.getExplicitLocationSearchValue(VizService.showShareParamKey));
    }
    return null;
  }

   /**
   * returns true if format search param is given with export type in url
   * Example: ?:format=pdf
   */
  public isFormatAsExportParamSpecified() {
    return VizService.VizExportFormatTypeRegex.test(this.getExplicitLocationSearchValue(VizService.FormatParamKey));
  }

  /**
   * returns true if the end of the location path has a viz export file type.
   * Example: /workbook/view.png
   */
  public pathHasExportableFileType() {
    return VizService.VizExportFileTypeRegex.test(this.$location.path());
  }

  public redirectToEmbeddedMode(shouldRedirectWithoutAddingToHistory: boolean, event?: ng.IAngularEvent, options: {isFromGuest?: boolean, needsEmbedParam?: boolean, shouldPreventDefault?: boolean} = {}) {
    const alertId = this.getDataAlertId();
    if (!this.server.inSession() || !alertId) {
      const url = this.getEmbeddedVizqlUrlForNonDataAlerts(options);
      if (!shouldRedirectWithoutAddingToHistory) {
        // When navigating from an external page to vizportal on a phone, our decision to
        // automatically redirect you to embedded mode can result in really strange behavior
        // with your browser history. The first redirect happens, but triggers the
        // $locationChangeStart handler, which causes a second redirect. To prevent this
        // second redirect from happening and mucking with the user's browser history,
        // we should only perform it when there hasn't already been a redirect processed.
        if (!this.didAlreadyDoRedirect) {
          this.WindowLocationService.navigateTo(url);
        } else {
          // Since VizService persists across requests, set didAlreadyDoRedirect back to
          // its default state for the next time we have to do a redirect.
          this.didAlreadyDoRedirect = false;
        }
      } else {
        this.WindowLocationService.navigateToWithoutAddingToHistory(url);
        this.didAlreadyDoRedirect = true;
      }
    } else {
      // getEmbeddedVizqlUrlForDataAlerts returns a promise, so WindowLocationService navigates to the url returned by the callback in the promise
      this.getEmbeddedVizqlUrlForDataAlerts(alertId, options).then((url:string) => {
        this.WindowLocationService.navigateToWithoutAddingToHistory(url);
        // for the embedded viz case, we want to call event.preventDefault on the $locationChangeStart event only
        // after the promise is resolved, so it was moved out of the event listener and into this block of code
        if (event && options.shouldPreventDefault) {
            event.preventDefault();
        }
      });
    }
  }

  // TFSID949509
  // this looks like it should also include redirect_to_favorite_view but everything is working fine??
  // my guess is that this can be deleted after all the viz code moves to react router. Or now. 
  public shouldRedirectWithoutAddingToHistory(oldUrl: string) {
    // The /redirect_to_view URL is not supposed to add itself to the user's browser
    // history, since it uses {location: 'replace'}. However, there is potentially
    // a bug in our version of angular-ui-router, since it shows up in the history
    // anyway. This causes issues when we redirect to the embedded version of a view,
    // since hitting the back button sends us to /redirect_to_view, which sends us
    // back to the view we were just looking at.
    //
    // To fix this, try and detect when the oldUrl is /redirect_to_view. If it is,
    // we will return true to eventually tell the WindowLocationService to
    // navigate the user to the view without adding to the history. This function can be
    // simplified to "return false;" if and when we update the router so that
    // /redirect_to_view does not add itself to the history.
    return this.isRedirectingToVizUrl(oldUrl);
  }

  public isRedirectingToVizUrl(url: string): boolean {
    const regexString_Redirect = '.*/redirect_to_view/[0-9]+';
    const regExp_Redirect = new RegExp(regexString_Redirect);

    const regexString_FavoriteRedirect = '.*/redirect_to_favorite_view/[0-9]+';
    const regExp_FavoriteRedirect = new RegExp(regexString_FavoriteRedirect);
    const isUrlRedirectToView = regExp_Redirect.test(url) || regExp_FavoriteRedirect.test(url);
    return isUrlRedirectToView;
  }

   /**
   * Go to embedded version of the current url if it is not a data alert.
   * Throws an Error if the current url is a data alert. If embed param
   * is not on, we pass in needsEmbedParam as true so that getVizqlUrl turns it on.
   */
  public getEmbeddedVizqlUrlForNonDataAlerts(options: {isFromGuest?: boolean} = {}) {
    return this.getVizqlUrl({isFromGuest: options.isFromGuest, needsEmbedParam: !this.isEmbedParamOn()});
  }

  /**
   * Go to embedded version of the current url if it is a data alert.
   * Throws an Error if the current url is not a data alert. If embed param
   * is not on, we pass in needsEmbedParam as true so that getVizqlUrl turns it on.
   */
  public getEmbeddedVizqlUrlForDataAlerts(alertId: string, options: {isFromGuest?: boolean} = {}): IPromise<IDataAlertInfo | string> {
    return this.dataAlerts.getById(alertId)
      .then((dataAlert: IDataAlertInfo) => {
        this.$location.search(UrlParamKeys.DeviceType, dataAlert.deviceType.toLowerCase());
        return this.getVizqlUrl({isFromGuest: options.isFromGuest, needsEmbedParam: !this.isEmbedParamOn()});
      }).catch((error: TypeError) => {
        Tableau.Logger().error('Unable to find valid alert with Id: ' + alertId);
        return this.getVizqlUrl({isFromGuest: options.isFromGuest});
      });
  }

  public getVizqlUrl(options: {isFromGuest?: boolean, needsEmbedParam?: boolean} = {}) {
          if (options.isFromGuest) {
              this.$location.search(VizService.IsGuestRedirectFromVizPortalParamKey, 'y');
          }
          if (options.needsEmbedParam) {
              this.$location.search(VizService.EmbedParamKey, 'y');
          }
          return this.$location.url().replace(/^\/site/, '/t');
      }

  private dontReloadNextLocationChange($scope: ng.IScope) {
          // (myork 2014-10-7) We want to change the $location, but we don't want ui-router to reload
          // the state. To do this we set this $state to `reloadOnSearch: false` until after the
          // location change is successful. This is disgusting for three reasons:
          //  1. It changes state configuration at runtime.
          //  2. It relies on `reloadOnSearch: false` not reloading the state when path parameters change.
          //     This is a bug in ui-router and we are relying on it. Once ui-router's dynamic parameters
          //     feature is implemented, we should be able to use that:
          //     https://github.com/angular-ui/ui-router/issues/1079
          //  3. We rely on this $locationChangeSuccess firing after ui-router's own $locationChangeSuccess
          //     listener. If this $locationChangeSuccess fired before ui-router's then we'd set
    //     `reloadOnSearch: true` before the $stateChangeStart, so we wouldn't get the no-reload
    //     behavior we're shooting for. I did not find a guarantee of this ordering in the docs,
    //     but in the code the events fire in the order they are registered:
    //     $on: https://github.com/angular/angular.js/blob/v1.2.x/src/ng/rootScope.js#L965
    //     $broadcast: https://github.com/angular/angular.js/blob/v1.2.x/src/ng/rootScope.js#L1077
    const removeEventListener = $scope.$on('$locationChangeSuccess', () => {
      this.$state.current.reloadOnSearch = true;
      removeEventListener();
    });

    this.$state.current.reloadOnSearch = false;
  }
}

Module.instance().service('VizService', VizService);
